import { useEffect } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import LivePreview from "./pages/__LivePreview";
import { useIsAuthenticated, useMsal} from "@azure/msal-react";
import { InteractionStatus } from "@azure/msal-browser";
import { savePostAuthRedirect } from "./utils/authRedirect";
import { sendAttribution } from "./utils/attribution";
import { parseAppSignin } from "./utils/appSignin";

import Login from "./pages/Login";
import Logout from "./pages/Logout";
import Connections from "./pages/Connections";
import Memory from "./pages/Memory";
import Account from "./pages/Account";
import Join from "./pages/Join";
import { HELP_URL } from "./utils/landing";
import Support from "./pages/Support";
import AgentDownload from "./pages/AgentDownload";
import Start from "./pages/Start";
import DeviceConnect from "./pages/DeviceConnect";
import AppSignedIn from "./pages/AppSignedIn";
import RedirectTo from "./pages/RedirectTo";
import Projects from "./pages/Projects";
import ProjectDetail from "./pages/ProjectDetail";
import UsageMonitor from "./pages/UsageMonitor";
import Dictionaries from "./pages/Dictionaries";
import DictionaryEdit from "./pages/DictionaryEdit";
import RunForm from "./pages/RunForm";
import RunDetail from "./pages/RunDetail";
import RunData from "./pages/RunData";
import Notebook from "./pages/Notebook";
import Main from "./layouts/Main";
import Settings from "./pages/Settings";
import Billing from "./pages/Billing";
import ImportSnowflake from "./pages/ImportSnowflake";
import Data from "./pages/Data";
import Analyze from "./pages/Analyze";
import AnalyzeChat from "./pages/AnalyzeChat";
import AnalyzeReport from "./pages/AnalyzeReport";

// const RequireAuth = ({ children }) => {
//   const isAuth = useIsAuthenticated();
//   return isAuth ? children : <Navigate to="/login" replace />;
// };

// `allowSignedOut`: the page signs in by itself (the iPhone's https sign-in goes straight to
// Microsoft's page, never Studio's /login), so a signed-out visit renders it instead of redirecting.
const RequireAuth = ({ children, allowSignedOut = false }) => {
  const isAuth = useIsAuthenticated();
  const { inProgress } = useMsal();

  // Signed in: send where this account came from, once (a no-op when there's nothing stored).
  useEffect(() => {
    if (isAuth && inProgress === InteractionStatus.None) sendAttribution();
  }, [isAuth, inProgress]);

  // Show a placeholder until MSAL finishes restoring the session
  if (inProgress !== InteractionStatus.None) {
    return <div>Loading...</div>;
    // or return a spinner component
  }

  if (!isAuth && !allowSignedOut) {
    // Remember the deep link so index.js can put the user back here after sign-in —
    // redirectUri is pinned to the bare origin, so without this every login lands on "/".
    savePostAuthRedirect();
    return <Navigate to="/login" replace />;
  }

  return children;
};

// /device: the iPhone's https sign-in (app=ios&return=https) handles signing in itself; every other
// link it serves keeps the /login screen.
function DeviceRoute() {
  const { search } = useLocation();
  const direct = Boolean(parseAppSignin(search)?.https);
  return <RequireAuth allowSignedOut={direct}><DeviceConnect /></RequireAuth>;
}

// A route whose target is another site (the landing page). React Router's Navigate is in-app only.
function ExternalRedirect({ href }) {
  useEffect(() => { window.location.replace(href); }, [href]);
  return null;
}

export default function App() {
  return (
    <Routes>
      {/* Public */}
      <Route path="/login" element={<Login />} />
      <Route path="/logout" element={<Logout />} />
      {/* The agent — one standalone page outside the analytics app shell, in its own nav (MemoryNav).
          Installing lives on the landing page beside the download; old install links go there. */}
      {/* The site's "Download for Mac": counts the download by channel, then starts the file. */}
      <Route path="/agent/download" element={<AgentDownload />} />
      <Route path="/connect" element={<Connections />} />
      {/* /plugin is where people are sent for their key (the agent's key fallback, the site's "Use a key
          instead"), so it keeps the sign-in; a bare /connect sends signed-out visitors to the site. */}
      <Route path="/plugin" element={<Navigate to="/connect?signin" replace />} />
      {/* The installed agent links to both of these (desktop menu, error pages, sign-in dialog).
          Neither existed, so they fell through the "*" fallback onto the analytics Projects page. */}
      <Route path="/help" element={<ExternalRedirect href={HELP_URL} />} />
      <Route path="/support" element={<Support />} />
      {/* /connect was the plugin page's name when the plugin was the product. Published links point
          at it (the landing page, docs), so it redirects for good. */}
      {/* Development-only visual harness for the Live tab's sections; see
          pages/__LivePreview.jsx. The NODE_ENV guard keeps it out of the build. */}
      {process.env.NODE_ENV === "development" && (
        <Route path="/__preview/live" element={<LivePreview />} />
      )}
      {/* /agent IS the account: the plan, this week's usage, the team and its seats. Nothing else —
          the agent is used in the terminal and the app; the site is where you pay for it. Analytics
          keeps its own /settings and /billing at "/". Old deep links resolve here. */}
      <Route path="/account" element={<Account />} />
      <Route path="/join/:token" element={<Join />} />
      <Route path="/agent" element={<Navigate to="/account" replace />} />
      <Route path="/agent/account" element={<Navigate to="/account" replace />} />
      <Route path="/agent/spaces/:slug" element={<Navigate to="/account" replace />} />
      {/* /memory is the plugin's page — the tree, receipts and recent captures for people who use the
          memory plugin in Claude Code, Codex or Cursor. It is baked into shipped plugins, so it stays a
          page. The agent has its own pages under /agent. */}
      <Route path="/memory" element={<Memory />} />
      {/* Where a fresh sign-in with no deep link lands: the agent by default, analytics for accounts
          that have projects. index.js and /login both send "nowhere in particular" here. */}
      <Route path="/start" element={<RequireAuth><Start /></RequireAuth>} />
      {/* Approve a sign-in from the Assertion terminal agent or Mac app (opened with ?code=XXXX-XXXX). */}
      <Route path="/device" element={<DeviceRoute />} />
      {/* Where the iPhone's https sign-in hands the grant back. The app's sign-in sheet catches this
          link before it loads; the page is only for a browser that renders it anyway. */}
      <Route path="/app/signin" element={<AppSignedIn />} />
      <Route path="/redirect/:where" element={<RedirectTo />} />

      {/* Admin usage monitor — standalone, OUTSIDE the analytics app shell so it
          never appears in the customer product (no sidebar entry, not shown while
          impersonating). Requires login; the page itself gates on isAdmin. */}
      <Route
        path="/admin/usage"
        element={<RequireAuth><UsageMonitor /></RequireAuth>}
      />

      {/* App shell + protected routes */}
      <Route
        path="/"
        element={
          <RequireAuth>
            <Main variant="app" />
          </RequireAuth>
        }
      >
        <Route index element={<Projects />} />
        <Route path="projects/:id" element={<ProjectDetail />} />
        <Route path="runs/new" element={<RunForm />} />
        <Route path="runs/:id" element={<RunDetail />} />
        <Route path="runs/:id/data" element={<RunData />} />
        <Route path="runs/:id/dictionary" element={<DictionaryEdit mode="snapshot" />} />
        <Route path="runs/:id/notebook" element={<Notebook />} />
        <Route path="dictionaries" element={<Dictionaries />} />
        <Route path="dictionaries/new" element={<DictionaryEdit />} />
        <Route path="dictionaries/:id" element={<DictionaryEdit />} />
        <Route path="dictionaries/import-snowflake" element={<ImportSnowflake />} />
        <Route path="settings" element={<Settings />} />
        <Route path="billing" element={<Billing />} />
        <Route path="data" element={<Data />} />
        {/* Both routes mount the same Analyze component — it branches on
            useParams().sessionId to either render the composer + history
            (no sessionId) or the chat (sessionId set). This avoids the
            jarring "page change" between Explore and the chat. */}
        <Route path="analyze" element={<Analyze />} />
        <Route path="analyze/:sessionId" element={<Analyze />} />
        <Route path="analyze/:sessionId/report" element={<AnalyzeReport />} />
        <Route path="projects/:projectId/analyze/:sessionId" element={<AnalyzeChat />} />
        <Route path="projects/:projectId/analyze/:sessionId/report" element={<AnalyzeReport />} />
      </Route>

      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}